import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { warning } from '../../../../../components/Alert/Alert';
import Checkbox from '../../../../../components/Checkbox/Checkbox';
import Input from '../../../../../components/Input/Input';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { EDissipation } from '../../../enum/general';
import { selectProject } from '../../../selector/conversion';
import { setHydraulicData } from '../../../store/project/projectSlice';
import { cleanResults } from '../../../store/project/resultsSlice';
import { IHydraulicDataProps, IHydraulicDataSteppedWeirForm } from './IHydraulicData';

const HydraulicDataSteppedWeir = (props: IHydraulicDataProps) => {
    let timer = useRef<NodeJS.Timeout | null>(null);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);

    const defaultValue: IHydraulicDataSteppedWeirForm = {
        discharge: (project.data?.hydraulicData as IHydraulicDataSteppedWeirForm)?.discharge,
        roughnessCoefficient: (project.data?.hydraulicData as IHydraulicDataSteppedWeirForm)?.roughnessCoefficient,
        dissipation: (project.data?.hydraulicData as IHydraulicDataSteppedWeirForm)?.dissipation,
        riverBedGradient: (project.data?.hydraulicData as IHydraulicDataSteppedWeirForm)?.riverBedGradient,
        dissipationType: (project.data?.hydraulicData as IHydraulicDataSteppedWeirForm)?.dissipationType,
        id: project.data?.hydraulicData?.id,
    };

    const [form, setForm] = useState<IHydraulicDataSteppedWeirForm>(defaultValue);
    const [disableDissipation, setDisableDissipation] = useState<boolean>(defaultValue.dissipationType === EDissipation.Automated);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let name = e.target.name;
        let value = parseFloat(e.target.value);
        if (name == 'dissipation' && value > 80) {
            value = 80;
            warning(t('dissipationMaxInfo'));
        }
        if (value < 0) {
            value = 0;
            warning(t('warning_not_be_lower', { value: 0 }));
        }

        setForm({
            ...form!,
            [name]: value,
        });
    };

    const handleAutomatedDissipation = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({
            ...form,
            dissipationType: e.target.checked ? EDissipation.Automated : EDissipation.Manual,
        });
        setDisableDissipation(!disableDissipation);
    };

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            dispatch(cleanResults());
            dispatch(setHydraulicData(form as IHydraulicDataSteppedWeirForm));
        }, 1000);
    }, [form]);

    return (
        <div className='d-flex flex-column gap-2'>
            <Input
                type='number'
                title={t('input_discharge', { unit_measure: conversionLabel.m3s_ft3s[projectInfo.data?.unitOfMeasure as number] })}
                value={form.discharge}
                name='discharge'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <Input
                type='number'
                title={t('input_riverBedGradient', { unit_measure: 'm/m' })}
                value={form.riverBedGradient}
                name='riverBedGradient'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <Input
                type='number'
                title={t('input_roughnessCoefficient')}
                value={form.roughnessCoefficient}
                name='roughnessCoefficient'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <Input
                type='number'
                title={t('input_dissipation')}
                value={form.dissipation}
                name='dissipation'
                className='d-flex justify-content-between align-items-center'
                disabled={disableDissipation}
                onChange={handleInputChange}
            />
            <Checkbox title={t('input_automated_dissipation')} name='dissipationType' defaultChecked={form.dissipationType === EDissipation.Automated} onChange={handleAutomatedDissipation} />
        </div>
    );
};

export default HydraulicDataSteppedWeir;
